import type { PopoverProps, Theme } from '@material-ui/core';
import { createStyles, Grid, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { i18n, Typography } from '@nutrien/cxp-components';
import { useSiteFeatures } from '@nutrien/minesight-utility-module';
import React, { useEffect, useState } from 'react';
import { useRxCollection } from 'rxdb-hooks';

import { useCurrentBorer } from '@/rxdb/Equipment/useCurrentBorer';
import useInspections, { InspectionMenuOption } from '@/rxdb/Inspections/useInspections';
import RxdbCollectionName from '@/rxdb/rxdbCollectionName';
import { sortInspectionMenu } from '@/utilities/sortHelper';
import { formatAsStartCase } from '@/utilities/utilityFunctions';

import { EquipmentTypeName, InspectionTab } from '../../../../utilities/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    popover: {
      borderRadius: '8px !important',
    },
    div: { backgroundColor: theme.palette.background.default },
    container: {
      padding: '16px',
      backgroundColor: 'rgba(56,56,56,1)',
    },
    title: {
      paddingBottom: '24px',
      color: theme.palette.text.secondary,
    },
    link: {
      padding: '10px 0px',
    },
  }),
);

interface Props extends PopoverProps {
  openAddDeficiency: () => void;
  onAddPreOpCheck: (inspectionId: string) => void;
}

const DeficienciesMenu = ({
  open,
  anchorEl,
  onClose,
  openAddDeficiency,
  onAddPreOpCheck,
}: Props) => {
  const classes = useStyles();
  const { isVanscoy, isCory } = useSiteFeatures();
  const { isRehabBorer, miningMethod } = useCurrentBorer();
  const { getInspectionsForMenu, inspectionsInitialized } = useInspections();
  const [inspectionMenuOptions, setInspectionMenuOptions] = useState<InspectionMenuOption[]>([]);
  const inspectionsCollection = useRxCollection(RxdbCollectionName.INSPECTIONS);

  const setUpMenu = async () => {
    const dynamicMenuOptions = await getInspectionsForMenu(
      InspectionTab.EQUIPMENT,
      miningMethod,
      isRehabBorer ? EquipmentTypeName.Rehab : EquipmentTypeName.Borer,
    );
    setInspectionMenuOptions(dynamicMenuOptions);
  };

  useEffect(() => {
    if (!inspectionsCollection || !inspectionsInitialized) return;
    const sub = inspectionsCollection.$.subscribe(() => {
      setUpMenu();
    });
    setUpMenu();
    return () => sub.unsubscribe();
  }, [inspectionsInitialized, inspectionsCollection, isRehabBorer]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      className={classes.popover}
    >
      <div className={classes.div}>
        <Grid container className={classes.container} style={{ width: isVanscoy ? 425 : 375 }}>
          {!(isRehabBorer && isVanscoy) && (
            <Grid item container xs={isVanscoy || isCory ? 8 : 6}>
              <Grid item xs={12} className={classes.title}>
                <Typography variant="h6">{i18n.t('Checklist')}</Typography>
              </Grid>
              {inspectionMenuOptions?.sort(sortInspectionMenu).map(inspectionOption => (
                <Grid
                  item
                  xs={12}
                  className={classes.link}
                  onClick={() => onAddPreOpCheck(inspectionOption.inspectionId)}
                  id={`add-${inspectionOption.inspectionId}`}
                  key={inspectionOption.inspectionId}
                >
                  <Typography variant="body1">
                    {formatAsStartCase(inspectionOption.description)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
          <Grid item xs={isVanscoy || isCory ? 4 : 6}>
            <Grid item xs={12} className={classes.title}>
              <Typography variant="h6">{i18n.t('Single entry')}</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              className={classes.link}
              onClick={openAddDeficiency}
              id="add-deficiency"
            >
              <Typography variant="body1">{i18n.t('Deficiency')}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Popover>
  );
};

export default DeficienciesMenu;
